/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import SqlImportsCreateQuery from 'cio/components/sql-imports/create/query';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { next } from '@ember/runloop';
import { SyncTarget } from 'cio/models/sql-import';
import { pluralize } from 'ember-inflector';
import ObjectTypesService from 'cio/services/object-types';
import { ImportType } from 'cio/utils/sql-import';

export default class SqlImportsCreateRelationshipQuery extends SqlImportsCreateQuery {
  @service environment!: EnvironmentService;
  @service objectTypes!: ObjectTypesService;

  @tracked hasSuccessfulQuery = false;

  readonly peopleIdField = [
    { label: 'By id', value: 'id' },
    { label: 'By email', value: 'email' },
  ];

  constructor(owner: unknown, args: SqlImportsCreateQuery['args']) {
    super(owner, args);

    let { relationship, idFieldName, importType } = args.model;

    next(() => {
      if (args.model.get('isNew') && relationship == null) {
        args.model.set('relationship', {
          entity1_type_id: 0,
          entity1_id_field_name: importType === ImportType.Object ? 'id' : idFieldName,
          entity2_type_id: Number(this.selectedObject?.id ?? this.fallbackObjectTypeId),
          entity2_id_field_name: importType === ImportType.Object ? idFieldName : 'object_id',
        });
      }
    });
  }

  get objectTypeOptions() {
    return this.objectTypes.enabledObjectTypes.map((ot) => ({
      label: ot.name,
      value: ot.id,
    }));
  }

  get fallbackObjectTypeId() {
    return this.objectTypes.sortedEnabledObjectTypes[0]?.id;
  }

  get selectedObjectTypeId() {
    return this.args.model?.relationship?.entity2_type_id.toString();
  }

  get objectType() {
    return this.objectTypes.objectTypes.find(({ id }) => id === this.selectedObjectTypeId);
  }

  get titleRelationCopy() {
    let { target, source } = this.getRelationshipTargetAndSource();

    return `How do these ${pluralize(source).toLowerCase()} relate to your ${target.toLowerCase()}?`;
  }

  get subtitleRelationCopy() {
    let { target } = this.getRelationshipTargetAndSource();

    if (target === 'People') {
      return `Include an <code>object_id</code> column and either an <code>id</code> or <code>email</code> column for the related person.`;
    }

    return `For people, include either an <code>id</code> or an <code>email</code>. For the objects they should be related to, first select the appropriate object type in the dropdown, then include an <code>object_id</code>.`;
  }

  @action
  onIdFieldChange(option: Record<string, any>) {
    this.args.model.set('relationship', {
      ...this.args.model.relationship,
      entity1_id_field_name: option.value,
    });
  }

  @action
  onObjectTypeIdFieldChange(option: Record<string, any>) {
    this.args.model.set('relationship', {
      ...this.args.model.relationship,
      entity2_type_id: Number(option.value),
    });
  }

  @action
  onQuery({ hasSuccessfulQuery }: { hasSuccessfulQuery: boolean }) {
    this.hasSuccessfulQuery = hasSuccessfulQuery;
    this.args.onQuery?.({ hasSuccessfulQuery: true, hasSuccessfulRelationshipsQuery: hasSuccessfulQuery });
  }

  @action onSkip() {
    this.analytics.track('sql_imports', 'skip_relationships_query', { model: this.args.model });
    this.args.model.set('relationship', null);
    this.args.onSubmit();
  }

  private getRelationshipTargetAndSource() {
    let objectName = this.selectedObject?.name ?? SyncTarget.Objects;
    let source = this.selectedObject?.name ?? SyncTarget.People;
    let target = source === SyncTarget.People ? objectName : SyncTarget.People;

    return { target, source };
  }
}
